

const localhost = window.location.host == "localhost:3000"

// Zoom meetings are created by our own backend (LangGraph) — no longer n8n/Elestio.
const ZOOM_API_BASE_URL = localhost ? "https://testwizkind.caryfy.ai/wizkind-mobile-ai" : "https://api.wizkind.com/wizkind-mobile-ai";


const ZOOM_ENDPOINTS: Record<ZoomLinkType, string> = {
    support: `${ZOOM_API_BASE_URL}/create-support-zoom-meeting`,
    marketing: `${ZOOM_API_BASE_URL}/create-marketing-zoom-meeting`,
    hello: `${ZOOM_API_BASE_URL}/create-hello-zoom-meeting`,
};

export type ZoomLinkType = 'support' | 'marketing' | 'hello';

export interface ZoomMeetingResult {
    join_url: string;
    meeting_id: string;
}

export interface ZoomMeetingPayload {
    title: string;
    agency_name: string;
    start_datetime: string;       // wall-clock time in the meeting's timezone, trailing "Z" (e.g. 2026-07-31T19:30:00Z)
    duration_minutes: number;
    internal_members: { id: string | number; name: string; email: string }[];
    external_members: { name: string; email: string; uuid?: string; client_location_id?: string }[];
    host_name: string;
    meeting_type: string;
    link_type: ZoomLinkType;
    project_name?: string;
    goal_name?: string;
}

/**
 * Calls the backend Zoom endpoint for the given account:
 *  - 'support'   → create-support-zoom-meeting
 *  - 'marketing' → create-marketing-zoom-meeting
 *  - 'hello'     → create-hello-zoom-meeting
 */
export async function createZoomMeeting(
    payload: ZoomMeetingPayload,
    linkType: ZoomLinkType = 'support'
): Promise<ZoomMeetingResult> {
    const response = await fetch(ZOOM_ENDPOINTS[linkType] ?? ZOOM_ENDPOINTS.support, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
    });

    const text = await response.text();
    let data: any = null;
    try {
        data = text ? JSON.parse(text) : null;
    } catch {
        data = null;
    }

    // Backend returns 409 (MEETING_CONFLICT / DUPLICATE_REQUEST), 400, 502 with a JSON message
    if (!response.ok) {
        throw new Error(data?.message || `Zoom meeting creation failed (${response.status}): ${text}`);
    }

    // Structured error with a 200 status
    if (data?.success === false || data?.error) {
        const userMessage = data?.message || data?.error || "Zoom meeting creation failed.";
        throw new Error(userMessage);
    }

    // Response may be nested or flat — handle both
    const zoom = data?.zoom ?? data?.meeting ?? data;

    const join_url =
        zoom?.join_url ?? zoom?.meeting_link ?? zoom?.link ?? data?.join_url ?? "";
    const meeting_id = String(
        zoom?.id ?? zoom?.meeting_id ?? data?.meeting_id ?? ""
    );

    if (!join_url) {
        throw new Error(
            `Backend did not return a Zoom meeting link. Response: ${JSON.stringify(data)}`
        );
    }

    return { join_url, meeting_id };
}
